import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Box,
  TextField,
  Button,
  Typography,
  Stepper,
  Step,
  StepLabel,
  LinearProgress,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { SendOtp, ValidateOtp } from "../../services/auth-service/authService.js";
import { useNavigate } from "react-router-dom";
import pontis from '../../assets/images/pontis.png';

const steps = ["Set Password", "Verify OTP"];

const passwordStrength = (pwd) => {
  if (pwd.length < 12) {
    let score = 0;
    if (/[A-Z]/.test(pwd)) score++;
    if (/[a-z]/.test(pwd)) score++;
    if (/[0-9]/.test(pwd)) score++;
    if (/[^A-Za-z0-9]/.test(pwd)) score++;
    return Math.min(score, 3);
  }
  let score = 1;
  if (/[A-Z]/.test(pwd)) score++;
  if (/[a-z]/.test(pwd)) score++;
  if (/[0-9]/.test(pwd)) score++;
  if (/[^A-Za-z0-9]/.test(pwd)) score++;
  return score;
};

const validatePasswordPolicy = (password, identifier, name, org) => {
  if (password.length < 12) return "Minimum 12 characters required.";
  if (password.length > 64) return "Maximum 64 characters allowed.";
  if (!/[A-Z]/.test(password)) return "Must contain at least one uppercase letter.";
  if (!/[a-z]/.test(password)) return "Must contain at least one lowercase letter.";
  if (!/[0-9]/.test(password)) return "Must contain at least one number.";
  if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) return "Must contain at least one special character.";
  const p = password.toLowerCase();
  if (identifier && p.includes(identifier.toLowerCase())) return "Password cannot contain personal information.";
  if (identifier && identifier.includes('@')) {
    const [emailUsername, emailDomain] = identifier.toLowerCase().split('@');
    if (emailUsername?.length >= 3) {
      if (p.includes(emailUsername)) return "Password cannot contain personal information.";
      for (const w of (p.match(/[a-z]{4,}/g) || [])) if (emailUsername.includes(w)) return "Password cannot contain personal information.";
    }
    if (emailDomain) {
      const dn = emailDomain.split('.')[0];
      if (dn?.length >= 3) {
        if (p.includes(dn)) return "Password cannot contain personal information.";
        for (const w of (p.match(/[a-z]{4,}/g) || [])) if (dn.includes(w)) return "Password cannot contain personal information.";
      }
    }
  }
  if (name?.length >= 3) {
    const nl = name.toLowerCase();
    if (p.includes(nl)) return "Password cannot contain personal information.";
    for (const w of (p.match(/[a-z]{4,}/g) || [])) if (nl.includes(w)) return "Password cannot contain personal information.";
  }
  if (org?.length >= 3) {
    const ol = org.toLowerCase();
    if (p.includes(ol)) return "Password cannot contain personal information.";
    for (const w of (p.match(/[a-z]{4,}/g) || [])) if (ol.includes(w)) return "Password cannot contain personal information.";
  }
  if (/^(.)\1+$/.test(password)) return "Password cannot be all the same character.";
  if (/(012|123|234|345|456|567|678|789|890|abc|bcd|cde|def|efg|fgh|ghi|hij|ijk|jkl|klm|lmn|mno|nop|opq|pqr|qrs|rst|stu|tuv|uvw|vwx|wxy|xyz)/i.test(password))
    return "Password cannot contain sequential characters.";
  return null;
};

const strengthColor = (s) => s <= 1 ? "#e53935" : s <= 3 ? "#fb8c00" : "#43a047";
const strengthLabel = (s) => s <= 1 ? "Weak" : s <= 3 ? "Moderate" : "Strong";

const policyRules = (pw) => [
  { label: "12+ characters", ok: pw.length >= 12 },
  { label: "Upper & lowercase", ok: /[A-Z]/.test(pw) && /[a-z]/.test(pw) },
  { label: "Number", ok: /[0-9]/.test(pw) },
  { label: "Special character", ok: /[!@#$%^&*(),.?":{}|<>]/.test(pw) },
];

// Every colour token used on the RIGHT side — zero blue
const R = {
  pageBg:      "rgba(255,255,255,0.55)",
  cardBg:      "#ffffff",
  cardBorder:  "#e2e8f0",
  cardShadow:  "0 4px 20px rgba(15,23,42,0.07)",
  inputBg:     "#f8fafc",
  inputBorder: "#cbd5e1",
  inputFocus:  "#475569",
  labelColor:  "#64748b",
  labelFocus:  "#334155",
  headingColor:"#1e293b",
  subColor:    "#94a3b8",
  stepActive:  "#334155",   // stepper active circle — dark slate, NOT blue
  stepDone:    "#43a047",
  stepDefault: "#cbd5e1",
  stepConnector:"#e2e8f0",
  btnBg:       "#0a2352",   // navy CTA — not blue
  btnHover:    "#081b3d",
  btnDisabled: "#cbd5e1",
  otpBg:       "#f8fafc",
  otpBorder:   "#e2e8f0",
  otpRingFg:   "#334155",   // countdown ring fill — slate
  otpRingBg:   "#e2e8f0",
  otpText:     "#334155",
  resendColor: "#475569",
  resendBorder:"#94a3b8",
  footerText:  "#94a3b8",
};

export default function ForgotPasswordMUI() {
  const [step, setStep]           = useState(0);
  const [loading, setLoading]     = useState(false);
  const [showPwd, setShowPwd]     = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [timer, setTimer]         = useState(0);
  const [otpRequested, setOtpRequested] = useState(false);
  const navigate = useNavigate();

  const [form, setForm] = useState({ identifier: "", newPassword: "", confirmPassword: "", otp: "" });
  const [error, setError]     = useState("");
  const [success, setSuccess] = useState("");
  const [userInfo, setUserInfo] = useState({ name: "", org: "" });

  const strength = passwordStrength(form.newPassword);

  useEffect(() => {
    const c = sessionStorage.getItem("userCredentials");
    if (c) { const p = JSON.parse(c); setUserInfo({ name: p.user_name || "", org: p.org_name || "" }); }
  }, []);

  useEffect(() => {
    if (timer <= 0) { setOtpRequested(false); return; }
    const t = setTimeout(() => setTimer(timer - 1), 1000);
    return () => clearTimeout(t);
  }, [timer]);

  useEffect(() => {
    const expiry = sessionStorage.getItem("otpExpiry");
    if (expiry && Math.floor((expiry - Date.now()) / 1000) > 0) {
      sessionStorage.removeItem("resetToken");
      sessionStorage.removeItem("otpExpiry");
      setTimer(0); setOtpRequested(false); setStep(0);
    }
  }, []);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError(""); setSuccess("");
  };

  const handleSendOtp = async () => {
    const { identifier, newPassword, confirmPassword } = form;
    if (otpRequested && timer > 0) { alert(`Please wait ${timer}s.`); return; }
    if (!identifier || !newPassword || !confirmPassword) { setError("All fields are required."); return; }
    if (newPassword !== confirmPassword) { setError("Passwords do not match."); return; }
    const pe = validatePasswordPolicy(newPassword, identifier, userInfo.name, userInfo.org);
    if (pe) { setError(pe); return; }
    setLoading(true);
    try {
      const [headers, url] = SendOtp();
      const res = await axios.post(url, { email: identifier, new_password: newPassword }, headers);
      if (res.data.Status === "Success") {
        sessionStorage.setItem("resetToken", res.data.token);
        sessionStorage.setItem("otpExpiry", Date.now() + 60000);
        setOtpRequested(true); setSuccess("OTP sent. Check your email or phone."); setStep(1); setTimer(60);
      } else { setError(res.data.Message || "OTP sending failed."); }
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.message || "Server error.");
    } finally { setLoading(false); }
  };

  const handleVerifyOtp = async () => {
    const token = sessionStorage.getItem("resetToken");
    if (!form.otp) { alert("Enter OTP."); return; }
    setLoading(true);
    try {
      const [headers, url] = ValidateOtp();
      const res = await axios.post(url, { email: form.identifier, otp: form.otp, new_password: form.newPassword, token }, headers);
      if (res.status === 200) {
        sessionStorage.removeItem("resetToken");
        alert("Password reset successful. Please login.");
        navigate("/");
      }
    } catch (err) {
      alert(err.response?.data?.error || err.response?.data?.message || "Password reset failed.");
      setStep(0); setForm((p) => ({ ...p, otp: "" }));
      sessionStorage.removeItem("resetToken"); sessionStorage.removeItem("otpExpiry");
    } finally { setLoading(false); }
  };

  // ── shared input style — strictly no blue ──────────────────
  const inputSx = {
    mb: 1,
    "& .MuiOutlinedInput-root": {
      borderRadius: "7px",
      fontSize: 13,
      background: R.inputBg,
      "& .MuiOutlinedInput-notchedOutline": { borderColor: R.inputBorder },
      "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: R.inputFocus },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: R.inputFocus, borderWidth: 2 },
    },
    "& .MuiInputLabel-root": { fontSize: 13, color: R.labelColor },
    "& .MuiInputLabel-root.Mui-focused": { color: R.labelFocus },
    "& .MuiInputBase-input": { py: "9px" },
  };

  const primaryBtn = {
    mt: 1, mb: 0.5, py: 1.1,
    borderRadius: "7px",
    background: R.btnBg,
    fontSize: 13, fontWeight: 700, letterSpacing: 0.4, textTransform: "none",
    boxShadow: "0 3px 10px rgba(10,35,82,0.25)",
    "&:hover": { background: R.btnHover },
    "&:disabled": { background: R.btnDisabled },
  };

  return (
    <Box sx={{ height: "100vh", overflow: "hidden", display: "flex", flexDirection: "column", background: R.pageBg, position: "relative" }}>

      {/* ── FULL-SCREEN WATERMARK — sits behind everything, spans entire viewport ── */}
      <Box
        component="img" src={pontis} alt="" aria-hidden="true"
        sx={{
          position: "absolute", top: "45%", left: "45%",
          transform: "translate(-50%, -50%)",
          width: "50%", maxWidth: 820,
          opacity: 0.085,
          filter: "grayscale(1)",
          pointerEvents: "none", userSelect: "none", objectFit: "contain",
          zIndex: 0,
        }}
      />

      {/* ── TOP BANNER ── */}
      <Box sx={{
        flexShrink: 0,
        px: 4, py: 1.5, textAlign: "center",
        position: "relative", zIndex: 1,
      }}>
        <Typography sx={{ color: "#0a2352", fontSize: 35, fontWeight: 700, lineHeight: 1.3 }}>
          Thanks for Registering with us!!!
        </Typography>
      </Box>

      {/* ── BODY ── */}
      <Box sx={{ flex: 1, display: "flex", overflow: "hidden", position: "relative", zIndex: 1 }}>

        {/* ════════ LEFT PANEL ════════ */}
        <Box sx={{
          width: "65%", flexShrink: 0,
          display: "flex", flexDirection: "column", justifyContent: "center",
          px: 4, py: 3, position: "relative", overflow: "hidden",
        }}>
          {/* decorative circles */}
          <Box sx={{ position: "absolute", top: -50, right: -50, width: 160, height: 160, borderRadius: "50%", background: "#oa2352", pointerEvents: "none" }} />
          <Box sx={{ position: "absolute", bottom: -30, left: -30, width: 120, height: 120, borderRadius: "50%", background: "#oa2352", pointerEvents: "none" }} />

          <Box sx={{
            width: 250, height: 250, borderRadius: "12px",
            display: "flex", alignItems: "center", justifyContent: "center",
            mb: 2, fontSize: 230, position: "relative", zIndex: 1,
          }}>
            🛡️
          </Box>
          
          
          <Typography sx={{ color: "#oa2352", fontSize: 48, fontWeight: 700, lineHeight: 1.3, mb: 1, position: "relative", zIndex: 1,textAlign:'Center' }}>
            One small safety measure before we take you into our world.
          </Typography>

          {/* orange accent */}
          <Box sx={{ width: 44, height: 4, background: "#f97316", borderRadius: 2, mb: 3, position: "relative", zIndex: 1 }} />
        </Box>

        {/* ════════ RIGHT PANEL — zero blue ════════ */}
        <Box sx={{
          flex: 1, display: "flex", flexDirection: "column", justifyContent: "center",
          px: { xs: 3, md: 5 }, py: 2,
          background: "transparent", overflow: "hidden",
        }}>

          {/* stepper — ALL slate, no MUI default blue */}
          <Stepper
            activeStep={step}
            sx={{
              mb: 2,
              "& .MuiStepLabel-label": { fontSize: 12, fontWeight: 600, color: R.labelColor },
              "& .MuiStepLabel-label.Mui-active": { color: R.stepActive },
              "& .MuiStepLabel-label.Mui-completed": { color: R.stepDone },
              // icon circle colours — override MUI blue entirely
              "& .MuiStepIcon-root": { color: R.stepDefault, fontSize: 18 },
              "& .MuiStepIcon-root.Mui-active": { color: R.stepActive },
              "& .MuiStepIcon-root.Mui-completed": { color: R.stepDone },
              // the number inside the circle
              "& .MuiStepIcon-text": { fill: "#ffffff", fontSize: "0.6rem" },
              // connector line
              "& .MuiStepConnector-line": { borderColor: R.stepConnector },
            }}
          >
            {steps.map((s) => <Step key={s}><StepLabel>{s}</StepLabel></Step>)}
          </Stepper>

          {/* form card */}
          <Box sx={{
            borderRadius: "14px", p: 3,
            boxShadow: R.cardShadow,
            border: `1px solid #0a2352`,
          }}>

            {/* ── STEP 0 ── */}
            {step === 0 && (
              <>
                <Typography sx={{ fontSize: 18, fontWeight: 800, color: R.headingColor, mb: 0.25,textAlign: 'center' }}>
                  Reset Password
                </Typography>


                <TextField
                  fullWidth label="Email / Mobile" name="identifier"
                  value={form.identifier} onChange={handleChange}
                  sx={inputSx} size="small"
                />

                <TextField
                  fullWidth label="New Password" name="newPassword"
                  type={showPwd ? "text" : "password"}
                  value={form.newPassword} onChange={handleChange}
                  sx={inputSx} size="small"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowPwd(!showPwd)} size="small" disableRipple sx={{ color: R.labelColor }}>
                          {showPwd ? <VisibilityOff sx={{ fontSize: 16, color: "inherit" }} /> : <Visibility sx={{ fontSize: 16, color: "inherit" }} />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                {/* strength bar */}
                {form.newPassword && (
                  <Box sx={{ mb: 1 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.4 }}>
                      <Typography sx={{ fontSize: 11, color: R.subColor }}>Strength</Typography>
                      <Typography sx={{ fontSize: 11, fontWeight: 700, color: strengthColor(strength) }}>{strengthLabel(strength)}</Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={(strength / 5) * 100}
                      sx={{
                        height: 4, borderRadius: 2,
                        backgroundColor: R.cardBorder,
                        "& .MuiLinearProgress-bar": { backgroundColor: strengthColor(strength), transition: "background-color 0.4s" },
                      }}
                    />
                  </Box>
                )}

                {/* policy pills 2-col */}
                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0.5, mb: 1 }}>
                  {policyRules(form.newPassword).map((r, i) => (
                    <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
                      <Box sx={{
                        width: 12, height: 12, borderRadius: "50%", flexShrink: 0,
                        background: r.ok ? "#43a047" : R.stepDefault,
                        display: "flex", alignItems: "center", justifyContent: "center",
                      }}>
                        {r.ok && (
                          <svg width="7" height="5" viewBox="0 0 8 6" fill="none">
                            <path d="M1 3L3 5L7 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        )}
                      </Box>
                      <Typography sx={{ fontSize: 11, color: r.ok ? "#374151" : R.subColor }}>{r.label}</Typography>
                    </Box>
                  ))}
                </Box>

                <TextField
                  fullWidth label="Confirm Password" name="confirmPassword"
                  type={showConfirm ? "text" : "password"}
                  value={form.confirmPassword} onChange={handleChange}
                  sx={inputSx} size="small"
                  error={!!form.confirmPassword && form.confirmPassword !== form.newPassword}
                  helperText={form.confirmPassword && form.confirmPassword !== form.newPassword ? "Passwords do not match" : ""}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowConfirm(!showConfirm)} size="small" disableRipple sx={{ color: R.labelColor }}>
                          {showConfirm ? <VisibilityOff sx={{ fontSize: 16, color: "inherit" }} /> : <Visibility sx={{ fontSize: 16, color: "inherit" }} />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                {error && (
                  <Box sx={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: "7px", px: 1.5, py: 1, mb: 1 }}>
                    <Typography sx={{ color: "#dc2626", fontSize: 12 }}>{error}</Typography>
                  </Box>
                )}

                <Button fullWidth variant="contained" sx={primaryBtn} onClick={handleSendOtp} disabled={loading}>
                  {loading ? "Sending OTP…" : "Send OTP"}
                </Button>

              </>
            )}

            {/* ── STEP 1 ── */}
            {step === 1 && (
              <>
                <Typography sx={{ fontSize: 16, fontWeight: 700, color: R.headingColor, mb: 0.25 }}>
                  Verify your identity
                </Typography>
                <Typography sx={{ fontSize: 12, color: R.subColor, mb: 1.5 }}>
                  Enter the OTP sent to <strong style={{ color: R.stepActive }}>{form.identifier}</strong>.
                </Typography>

                {/* countdown — slate ring, no blue */}
                {timer > 0 && (
                  <Box sx={{
                    display: "flex", alignItems: "center", gap: 1.5,
                    background: R.otpBg, borderRadius: "9px",
                    px: 2, py: 1.25, mb: 1.5,
                    border: `1px solid ${R.otpBorder}`,
                  }}>
                    <Box sx={{
                      width: 38, height: 38, borderRadius: "50%", flexShrink: 0,
                      background: `conic-gradient(${R.otpRingFg} ${(timer / 60) * 360}deg, ${R.otpRingBg} 0deg)`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                      <Box sx={{
                        width: 27, height: 27, borderRadius: "50%",
                        background: R.otpBg,
                        display: "flex", alignItems: "center", justifyContent: "center",
                      }}>
                        <Typography sx={{ fontSize: 10, fontWeight: 800, color: R.otpText }}>{timer}s</Typography>
                      </Box>
                    </Box>
                    <Typography sx={{ fontSize: 12, color: R.resendColor }}>
                      OTP valid for <strong>{timer}s</strong>. Don't close this tab.
                    </Typography>
                  </Box>
                )}

                <TextField
                  fullWidth label="Enter OTP" name="otp"
                  value={form.otp} onChange={handleChange}
                  sx={inputSx} size="small"
                  inputProps={{ maxLength: 6, style: { letterSpacing: 10, fontSize: 20, fontWeight: 700, textAlign: "center" } }}
                />

                {success && (
                  <Box sx={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: "7px", px: 1.5, py: 1, mb: 1 }}>
                    <Typography sx={{ color: "#16a34a", fontSize: 12 }}>{success}</Typography>
                  </Box>
                )}
                {error && (
                  <Box sx={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: "7px", px: 1.5, py: 1, mb: 1 }}>
                    <Typography sx={{ color: "#dc2626", fontSize: 12 }}>{error}</Typography>
                  </Box>
                )}

                <Button fullWidth variant="contained" sx={primaryBtn} onClick={handleVerifyOtp} disabled={loading}>
                  {loading ? "Verifying…" : "Verify & Reset Password"}
                </Button>

                <Button
                  fullWidth disabled={timer > 0} onClick={handleSendOtp}
                  sx={{
                    mt: 0.75, py: 0.9, borderRadius: "7px",
                    textTransform: "none", fontSize: 12, fontWeight: 600,
                    color: timer > 0 ? R.stepDefault : R.resendColor,
                    border: `1px solid ${timer > 0 ? R.otpBorder : R.resendBorder}`,
                    background: "transparent",
                    "&:hover": { background: "#f8fafc" },
                  }}
                >
                  {timer > 0 ? `Resend OTP in ${timer}s` : "Resend OTP"}
                </Button>
              </>
            )}
          </Box>

        </Box>
      </Box>
    </Box>
  );
}